"use client";

import { useState, useEffect } from "react";
import { ISpot } from "@/types/spots/spot";
import { fetchAllBrands } from "@/utils/fetchs/fetchBrands";
import { fetchSpots } from "@/utils/fetchs/fetchSpots";
import { IFilters } from "@/types/spots/filters";

export function useFilters() {
  const [filters, setFilters] = useState<IFilters>({
    search_query: "",
    brand_name: "",
    model: "",
    min_rating: 0,
    max_rating: 100,
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [spots, setSpots] = useState<ISpot[]>([]);
  const [allBrands, setAllBrands] = useState<string[]>([]);
  const [filteredModels, setFilteredModels] = useState<string[]>([]);
  const [brandsCount, setBrandsCount] = useState(0);
  const [modelsCount, setModelsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9);

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, [key]: value };
  
      if (key === "brand_name") {
        // Si on change de marque, on remet toujours le model à vide
        newFilters.model = "";
        setFilteredModels([]);
      }
  
      // Réinitialiser le nombre de spots visibles
      setVisibleCount(10);
  
      return newFilters;
    });
  };
  

  // Récupération des marques
  useEffect(() => {
    (async () => {
      try {
        const brands = await fetchAllBrands();
        setAllBrands(brands);
        setBrandsCount(brands.length);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  // Debounce des filtres
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300);
    return () => clearTimeout(handler);
  }, [filters]);

  // Fetch des spots lorsque debouncedFilters change
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const data = await fetchSpots(debouncedFilters);
        setSpots(data);

        if (debouncedFilters.brand_name) {
          const models = data
            .filter((spot) => spot.brand_name.toLowerCase() === debouncedFilters.brand_name.toLowerCase())
            .map((spot) => spot.model);

          const uniqueModels = Array.from(new Set(models.map((m) => m.trim().toLowerCase())));
          setFilteredModels(uniqueModels);
          setModelsCount(uniqueModels.length);
        } else {
          setFilteredModels([]);
          setModelsCount(0);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [debouncedFilters]);

  return {
    filters,
    handleFilterChange,
    allBrands,
    brandsCount,
    filteredModels,
    modelsCount,
    isLoading,
    spots,
    visibleCount,
    setVisibleCount,
  };
}
